import React, { useEffect, useMemo, useState } from 'react';
import { Control, Controller, FieldErrors, UseFormClearErrors, UseFormSetError, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import Select from 'react-select';
import clsx from 'clsx';
import { CheckAviResponseType, RoomTypeData } from '../../response/CheckAviResponse';
import { RoomSelected } from './CreateBookingComponentV4';
import { CreateBookingFormV4 } from '../../../../services/create-booking-v4/schema/CreateBookingSchemaV4';

// Tipe untuk props utama
interface RoomSelectionPropsV4 {
    index: number;
    control: Control<CreateBookingFormV4>;
    watch: UseFormWatch<CreateBookingFormV4>;
    setValue: UseFormSetValue<CreateBookingFormV4>;
    setError: UseFormSetError<CreateBookingFormV4>;
    clearErrors: UseFormClearErrors<CreateBookingFormV4>;
    errors: FieldErrors<CreateBookingFormV4>;
    roomAvi: CheckAviResponseType;
    roomSelected: RoomSelected[];
    setRoomSelected: React.Dispatch<React.SetStateAction<RoomSelected[]>>;
    // Mode edit: kalau diisi, perubahan rate plan di-intercept lewat callback ini
    // (untuk menampilkan confirm dialog "pakai harga rate plan / tetap harga original")
    // alih-alih langsung commit ke form. Mode create tidak mengisi ini -> perilaku lama.
    isUpdateMode?: boolean;
    onRequestRatePlanChange?: (newRatePlanId: string, newRatePlanName?: string) => void;
}

// Tipe untuk opsi dropdown
type OptionType = { value: string; label: string; };

// --- Komponen Anak: Didefinisikan di sini untuk kemudahan copy-paste ---

const SelectRoomTypeV4: React.FC<any> = ({ control, index, options, errors, setValue }) => (
    <div className="col-12 mb-3">
        <label className="col-form-label">Room Type:</label>
        <Controller
            control={control}
            name={`bookingroom.${index}.room_type_id`}
            render={({ field }) => (
                <Select
                    className={clsx({ 'is-invalid': errors.bookingroom?.[index]?.room_type_id })}
                    options={options}
                    value={options.find((opt: OptionType) => opt.value === field.value)}
                    onChange={(opt) => {
                        field.onChange(opt?.value ?? '');
                        // PERBAIKAN: Hapus `{ shouldValidate: true }` untuk mencegah validasi prematur
                        setValue(`bookingroom.${index}.room_id`, '', { shouldDirty: true });
                        setValue(`bookingroom.${index}.rate_plan_id`, '', { shouldDirty: true });
                        // Bug: ganti room type tidak membersihkan `rates`, sehingga Total
                        // Accommodation tetap menampilkan angka dari room type SEBELUMNYA
                        // (nyangkut/tidak refresh) sampai user memilih rate plan baru untuk
                        // room type ini. Kosongkan supaya section total langsung ikut
                        // ter-reset (fallback "-") sampai rate plan baru dipilih & fetch selesai.
                        setValue(`bookingroom.${index}.rates`, [], { shouldDirty: true });
                    }}
                    noOptionsMessage={() => 'No rooms available'}
                />
            )}
        />
        {errors.bookingroom?.[index]?.room_type_id && <div className="invalid-feedback">{errors.bookingroom[index].room_type_id.message}</div>}
    </div>
);

const SubroomComponentV4: React.FC<any> = ({ watch, control, index, roomAvi, errors, roomSelected, setRoomSelected, setError, clearErrors }) => {
    const [subroomOptions, setSubroomOptions] = useState<OptionType[]>([]);
    const roomTypeId = watch(`bookingroom.${index}.room_type_id`);

    useEffect(() => {
        if (!roomTypeId) {
            setSubroomOptions([]);
            return;
        }
        const roomData = roomAvi.data.find((r: RoomTypeData) => r.id === roomTypeId);
        const rooms = roomData?.rooms?.map((r: { id: string, room_number: string }) => ({ value: r.id, label: r.room_number })) || [];
        setSubroomOptions([{ value: null, label: 'Unassigned' }, ...rooms]);
    }, [roomTypeId, roomAvi]);

    return (
        <div className="col-12 mb-3">
            <label className="col-form-label">Room Number:</label>
            <Controller
                control={control}
                name={`bookingroom.${index}.room_id`}
                render={({ field }) => (
                    <Select
                        className={clsx({ 'is-invalid': errors.bookingroom?.[index]?.room_id })}
                        options={subroomOptions}
                        value={subroomOptions.find((opt) => opt.value === field.value) || null}
                        onChange={(opt) => {
                            const newRoomId = opt?.value ?? null;
                            if (newRoomId) {
                                const isSelected: boolean = roomSelected.some((r: RoomSelected) => r.room_id === newRoomId && r.index !== index);
                                if (isSelected) {
                                    setError(`bookingroom.${index}.room_id`, { message: 'Room already selected.' });
                                    return;
                                }
                            }
                            clearErrors(`bookingroom.${index}.room_id`);
                            // PERBAIKAN: Secara eksplisit memberikan tipe pada parameter 'item'
                            interface SetRoomSelectedItem {
                                index: number;
                                room_type_id: string;
                                room_id: string | null;
                            }

                            setRoomSelected((prev: RoomSelected[]) => [
                                ...prev.filter((item: RoomSelected) => item.index !== index),
                                { index, room_type_id: roomTypeId as string, room_id: newRoomId }
                            ] as SetRoomSelectedItem[]);
                            field.onChange(newRoomId);
                        }}
                    />
                )}
            />
            {errors.bookingroom?.[index]?.room_id && <div className="invalid-feedback">{errors.bookingroom[index].room_id.message}</div>}
        </div>
    );
};

const RateplansComponentV4: React.FC<any> = ({ index, control, watch, roomAvi, errors, isUpdateMode, onRequestRatePlanChange }) => {
    const [ratePlanOptions, setRatePlanOptions] = useState<OptionType[]>([]);
    const roomTypeId = watch(`bookingroom.${index}.room_type_id`);

    useEffect(() => {
        if (!roomTypeId) {
            setRatePlanOptions([]);
            return;
        }
        const roomData = roomAvi.data.find((r: RoomTypeData) => r.id === roomTypeId);
        const plans = roomData?.rate_plans?.map((p: { id: string, name: string }) => ({ value: p.id, label: p.name })) || [];
        setRatePlanOptions(plans);
    }, [roomTypeId, roomAvi]);

    // Rate plan yang tidak muncul di dropdown karena stay lebih pendek dari min_night-nya.
    const roomData: RoomTypeData | undefined = roomAvi.data.find((r: RoomTypeData) => r.id === roomTypeId);
    const excludedPlans = roomData?.rate_plans_excluded ?? [];
    const nights = roomData?.nights;
    const stayLabel = nights ? `${nights} night${nights > 1 ? 's' : ''}` : 'this stay';
    const excludedText = excludedPlans.map((p) => `${p.name} (min. ${p.min_night} nights)`).join(', ');

    return (
        <div className="col-12">
            <label className="col-form-label">Rate Plan:</label>
            <Controller
                control={control}
                name={`bookingroom.${index}.rate_plan_id`}
                render={({ field }) => (
                    <Select
                        className={clsx({ 'is-invalid': errors.bookingroom?.[index]?.rate_plan_id })}
                        options={ratePlanOptions}
                        noOptionsMessage={() => excludedPlans.length > 0
                            ? 'No rate plan fits this length of stay'
                            : 'No rate plan available for this room type'}
                        value={ratePlanOptions.find((opt) => opt.value === field.value) || null}
                        onChange={(opt) => {
                            const newValue = opt?.value ?? '';
                            // Mode edit + benar-benar ganti rate plan (bukan clear / pilih ulang
                            // nilai yang sama) -> minta konfirmasi dulu lewat parent, jangan
                            // langsung commit supaya harga tidak silently berubah.
                            if (isUpdateMode && onRequestRatePlanChange && newValue && newValue !== field.value) {
                                onRequestRatePlanChange(newValue, opt?.label);
                                return;
                            }
                            field.onChange(newValue);
                        }}
                    />
                )}
            />
            {errors.bookingroom?.[index]?.rate_plan_id && <div className="invalid-feedback">{errors.bookingroom[index].rate_plan_id.message}</div>}
            {excludedPlans.length > 0 && ratePlanOptions.length === 0 && (
                <div className="alert alert-warning mt-2 mb-0 py-2" role="alert">
                    No rate plan can be used for {stayLabel}. Every rate plan of this room type needs a longer stay: {excludedText}.
                    Please extend the check-out date or choose another room type.
                </div>
            )}
            {excludedPlans.length > 0 && ratePlanOptions.length > 0 && (
                <div className="form-text">
                    Not shown because the stay is too short ({stayLabel}): {excludedText}.
                </div>
            )}
            {/* Hanya kalau backend sudah mengirim rate_plans_excluded; backend lama tidak bisa membedakan penyebabnya. */}
            {roomData?.rate_plans_excluded !== undefined && excludedPlans.length === 0 && ratePlanOptions.length === 0 && (
                <div className="alert alert-warning mt-2 mb-0 py-2" role="alert">
                    This room type has no rate plan yet. Please set up a rate plan first (Room &amp; Rate Settings).
                </div>
            )}
        </div>
    );
};

// Komponen Utama untuk file ini
const RoomSelectionV4: React.FC<RoomSelectionPropsV4> = (props) => {
    const roomTypeOptions = useMemo(() =>
        props.roomAvi.data.map((room: RoomTypeData) => ({ value: room.id, label: room.name })),
        [props.roomAvi]);

    return (
        <>
            <SelectRoomTypeV4 {...props} options={roomTypeOptions} />
            {props.watch(`bookingroom.${props.index}.room_type_id`) && (
                <>
                    <SubroomComponentV4 {...props} />
                    <RateplansComponentV4 {...props} />
                </>
            )}
        </>
    );
};

export default RoomSelectionV4;